import React, { Children } from 'react'
import styled from '@emotion/styled'
import flattenChildren from 'react-keyed-flatten-children'
import { isInteger as _isInteger } from 'lodash'

import { Box } from '../Box/Box'
import { SpaceProp } from '../Box/Box.types'
import { useScreenSize } from '../../hooks/useScreenSize'
import useResponsiveProp from '../../hooks/useResponsiveProp'

export interface InlineProps extends BaseProps {
  children: React.ReactNode
  space?: ResponsiveProp<SpaceProp>
  alignX?: ResponsiveProp<'start' | 'center' | 'end' | 'between' | 'evenly'>
  alignY?: ResponsiveProp<'top' | 'center' | 'bottom'>
  nowrap?: ResponsiveProp<boolean>
  grow?: (number | null)[]
}

function isInteger(x: any): x is number {
  return _isInteger(x)
}

const InlineComponent: React.FC<InlineProps> = ({
  space,
  alignX,
  alignY = 'top',
  nowrap,
  grow,
  ...props
}) => {
  const stackItems = flattenChildren(props.children)

  const device = useScreenSize()

  const responsiveSpace = useResponsiveProp(space, device)
  const responsiveAlignX = useResponsiveProp(alignX, device)
  const responsiveAlignY = useResponsiveProp(alignY, device)
  const responsiveNowrap = useResponsiveProp(nowrap, device)

  return (
    <Box negativeMarginTop={responsiveSpace}>
      <Box
        display="flex"
        flexWrap={responsiveNowrap ? 'nowrap' : 'wrap'}
        justifyContent={responsiveAlignX}
        alignItems={responsiveAlignY}
        negativeMarginLeft={responsiveSpace}
      >
        {Children.map(stackItems, (child, i) => {
          let flexGrow = grow && grow[i] ? grow[i] : undefined

          if (!isInteger(flexGrow)) {
            flexGrow = undefined
          }

          return (
            <Box
              marginLeft={responsiveSpace}
              marginTop={responsiveSpace}
              flexGrow={flexGrow}
              display="flex"
            >
              {child}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export const Inline = styled(InlineComponent)<InlineProps>`
  display: block;
  padding-top: 1px;
`
